import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileDown, FileText } from 'lucide-react';
import { useObras } from '../../context/ObrasContextSupabase';
import GerenciadorItens from './GerenciadorItens';
import { RelatorioGerencial } from '../relatorios';
import ModalMedicaoEmLote from './ModalMedicaoEmLote';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

const GerenciadorMedicoes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { obterObraPorId, atualizarObra } = useObras();
  const [obra, setObra] = useState(null);
  const [itens, setItens] = useState([]);
  const [mostrarRelatorio, setMostrarRelatorio] = useState(false);
  const [modalMedicaoAberto, setModalMedicaoAberto] = useState(false);

  useEffect(() => {
    if (id) {
      const obraData = obterObraPorId(id);
      if (obraData) {
        setObra(obraData);
        setItens(obraData.itens || []);
      } else {
        toast.error('Obra não encontrada');
        navigate('/');
      }
    }
  }, [id, obterObraPorId, navigate]);

  const salvarMedicoes = async () => {
    try {
      if (!obra) return;
      
      // Recalcular saldos para todos os itens antes de salvar
      const itensComSaldosRecalculados = itens.map(item => {
        // Ordenar detalhes por data
        const detalhesOrdenados = Array.isArray(item.detalhes) ? 
          [...item.detalhes].sort((a, b) => new Date(a.data) - new Date(b.data)) : 
          [];
        
        // Inicializar com o valor total do item
        const qtdTotal = parseFloat(item.quantidade) || 0;
        const valorTotal = parseFloat(item.total) || 0;
        
        console.log(`Recalculando saldos para item ${item.item} antes de salvar:`, {
          id: item.id,
          quantidade: qtdTotal,
          valorTotal: valorTotal,
          numDetalhes: detalhesOrdenados.length
        });
        
        let saldoQuantidade = qtdTotal;
        let saldoValor = valorTotal;
        
        // Calcular saldos para cada detalhe
        const detalhesComSaldos = detalhesOrdenados.map((detalhe, index) => {
          // Garantir que os valores são números
          const qtdDetalhe = parseFloat(detalhe.quantidade) || 0;
          const valorDetalhe = parseFloat(detalhe.valor) || 0;
          const totalDetalhe = qtdDetalhe * valorDetalhe;
          
          console.log(`Detalhe ${index + 1} do item ${item.item}:`, {
            data: detalhe.data,
            quantidade: qtdDetalhe,
            valor: valorDetalhe,
            total: totalDetalhe,
            saldoAntes: { qtd: saldoQuantidade, valor: saldoValor }
          });
          
          // Deduzir do saldo a quantidade e valor da medição atual
          saldoQuantidade -= qtdDetalhe;
          saldoValor -= totalDetalhe;
          
          // Garantir que os saldos não sejam negativos
          const saldoQtdFinal = Math.max(0, saldoQuantidade);
          const saldoValorFinal = Math.max(0, saldoValor);
          
          console.log(`Saldo após detalhe ${index + 1} do item ${item.item}:`, { 
            qtd: saldoQtdFinal, 
            valor: saldoValorFinal 
          });
          
          return {
            ...detalhe,
            itemId: item.id,
            quantidade: qtdDetalhe,
            valor: valorDetalhe,
            total: totalDetalhe,
            saldoQuantidade: saldoQtdFinal,
            saldoValor: saldoValorFinal
          };
        });
        
        // Recalcular métricas do item
        const quantidadeMedida = detalhesComSaldos.reduce((total, d) => total + (parseFloat(d.quantidade) || 0), 0);
        const totalMedido = detalhesComSaldos.reduce((total, d) => total + (parseFloat(d.total) || 0), 0);
        // Usar a variável qtdTotal já declarada anteriormente
        const progresso = qtdTotal > 0 ? Math.min(100, (quantidadeMedida / qtdTotal) * 100) : 0;
        
        return {
          ...item,
          detalhes: detalhesComSaldos,
          quantidadeMedida,
          totalMedido,
          progresso
        };
      });
      
      // Calcular o total de quantidades medidas (não o valor monetário)
      const totalMedicoes = itensComSaldosRecalculados.reduce(
        (total, item) => total + (parseFloat(item.quantidadeMedida) || 0), 0
      );
      
      await atualizarObra(id, {
        ...obra,
        itens: itensComSaldosRecalculados,
        totalMedicoes: totalMedicoes
      });
      
      // Atualizar o estado local com os valores recalculados
      setItens(itensComSaldosRecalculados);
      
      toast.success('Medições salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar medições:', error);
      toast.error('Erro ao salvar medições');
    }
  };

  // Função para exportar itens para Excel
  const exportarItensParaExcel = () => {
    if (!itens || itens.length === 0) {
      toast.error('Não há itens para exportar!');
      return;
    }
    
    // Preparar dados dos itens
    const dadosItens = itens.map(item => ({
      'Item': item.item,
      'Descrição': item.descricao,
      'Unidade': item.unidade,
      'Preço Unitário (R$)': item.preco,
      'Quantidade': item.quantidade,
      'Total (R$)': item.total,
      'Quantidade Medida': item.quantidadeMedida || 0,
      'Total Medido (R$)': item.totalMedido || 0,
      'Progresso (%)': item.progresso ? item.progresso.toFixed(2) : '0.00'
    }));
    
    // Preparar dados dos detalhes
    const dadosDetalhes = [];
    itens.forEach(item => {
      if (item.detalhes && item.detalhes.length > 0) {
        item.detalhes.forEach(detalhe => {
          dadosDetalhes.push({
            'Item': item.item,
            'Descrição Item': item.descricao,
            'Data': detalhe.data,
            'Operação': detalhe.operacao,
            'Quantidade': detalhe.quantidade,
            'Valor Unitário (R$)': detalhe.valor,
            'Total (R$)': detalhe.total,
            'Saldo Quantidade': detalhe.saldoQuantidade,
            'Saldo Valor (R$)': detalhe.saldoValor
          });
        });
      }
    });
    
    // Criar workbook e adicionar worksheets
    const wb = XLSX.utils.book_new();
    
    // Adicionar planilha de itens
    const wsItens = XLSX.utils.json_to_sheet(dadosItens);
    XLSX.utils.book_append_sheet(wb, wsItens, "Itens");
    
    // Adicionar planilha de detalhes se houver
    if (dadosDetalhes.length > 0) {
      const wsDetalhes = XLSX.utils.json_to_sheet(dadosDetalhes);
      XLSX.utils.book_append_sheet(wb, wsDetalhes, "Detalhamentos");
    }
    
    // Adicionar planilha de resumo
    const totalItens = itens.reduce((acc, item) => acc + item.total, 0);
    const totalMedido = itens.reduce((acc, item) => acc + (item.totalMedido || 0), 0);
    const progressoGeral = totalItens > 0 ? (totalMedido / totalItens) * 100 : 0;
    
    const dadosResumo = [
      {
        'Descrição': 'Total de Itens',
        'Valor': itens.length
      },
      {
        'Descrição': 'Valor Total Orçado (R$)',
        'Valor': totalItens.toFixed(2)
      },
      {
        'Descrição': 'Valor Total Medido (R$)',
        'Valor': totalMedido.toFixed(2)
      },
      {
        'Descrição': 'Progresso Geral (%)',
        'Valor': progressoGeral.toFixed(2)
      }
    ];
    
    const wsResumo = XLSX.utils.json_to_sheet(dadosResumo);
    XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");
    
    // Salvar o arquivo
    const dataAtual = new Date().toISOString().split('T')[0];
    const nomeArquivo = obra.nome.replace(/[^a-zA-Z0-9]/g, '_');
    XLSX.writeFile(wb, `Medicoes_${nomeArquivo}_${dataAtual}.xlsx`);
    
    toast.success('Medições exportadas para Excel com sucesso!');
  };

  // Formatar valor monetário
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };
  
  // Formatar percentual
  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };
  
  // Calcular valor total dos itens
  const calcularValorTotalItens = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((total, item) => total + (item.total || 0), 0);
  };
  
  // Calcular progresso geral dos itens
  const calcularProgressoGeral = (itens) => {
    if (!itens || itens.length === 0) return 0;
    
    const totalValor = calcularValorTotalItens(itens);
    if (totalValor === 0) return 0;
    
    const totalMedido = itens.reduce((total, item) => total + (item.totalMedido || 0), 0);
    return (totalMedido / totalValor) * 100;
  };
  
  // Função para processar medições em lote
  const processarMedicoesEmLote = (medicoes) => {
    if (!medicoes || medicoes.length === 0) return;
    
    try {
      console.log('Processando medições em lote:', medicoes);
      
      // Criar uma cópia dos itens atuais
      const novosItens = [...itens];
      
      // Processar cada medição
      medicoes.forEach(medicao => {
        const { itemId, data, operacao, numeroMedicao, descricaoMedicao, quantidade, valor } = medicao;
        
        // Verificar se os valores são válidos
        const qtd = parseFloat(quantidade);
        const vlr = parseFloat(valor);
        
        if (isNaN(qtd) || isNaN(vlr)) {
          console.error('Valores inválidos na medição:', medicao);
          return;
        }
        
        // Encontrar o item correspondente
        const itemIndex = novosItens.findIndex(item => item.id === itemId);
        if (itemIndex === -1) {
          console.error('Item não encontrado:', itemId);
          return;
        }
        
        const item = novosItens[itemIndex];
        
        // Criar o novo detalhe
        const novoDetalhe = {
          id: Date.now() + Math.random(), // ID único
          data,
          operacao,
          numeroMedicao: parseInt(numeroMedicao),
          descricaoMedicao,
          quantidade: qtd,
          valor: vlr,
          total: qtd * vlr,
          // Inicializar os saldos com zero, serão calculados depois
          saldoQuantidade: 0,
          saldoValor: 0
        };
        
        // Adicionar o detalhe ao item
        if (!item.detalhes || !Array.isArray(item.detalhes)) {
          item.detalhes = [];
        }
        
        item.detalhes.push(novoDetalhe);
      });
      
      // Recalcular saldos e métricas para todos os itens afetados
      novosItens.forEach(item => {
        if (item.detalhes && Array.isArray(item.detalhes) && item.detalhes.length > 0) {
          // Ordenar detalhes por data
          item.detalhes.sort((a, b) => new Date(a.data) - new Date(b.data));
          
          // Calcular saldos
          const qtdTotal = parseFloat(item.quantidade) || 0;
          const valorTotal = parseFloat(item.total) || 0;
          
          console.log(`Recalculando saldos para item ${item.item}:`, {
            id: item.id,
            quantidade: qtdTotal,
            valorTotal: valorTotal,
            numDetalhes: item.detalhes.length
          });
          
          let saldoQuantidade = qtdTotal;
          let saldoValor = valorTotal;
          
          // Mapear os detalhes com os saldos calculados
          item.detalhes = item.detalhes.map((detalhe, index) => {
            // Garantir que os valores são números
            const qtdDetalhe = parseFloat(detalhe.quantidade) || 0;
            const valorDetalhe = parseFloat(detalhe.valor) || 0;
            const totalDetalhe = qtdDetalhe * valorDetalhe;
            
            console.log(`Detalhe ${index + 1} do item ${item.item}:`, {
              data: detalhe.data,
              quantidade: qtdDetalhe,
              valor: valorDetalhe,
              total: totalDetalhe,
              saldoAntes: { qtd: saldoQuantidade, valor: saldoValor }
            });
            
            // Deduzir do saldo a quantidade e valor da medição atual
            saldoQuantidade -= qtdDetalhe;
            saldoValor -= totalDetalhe;
            
            // Garantir que os saldos não sejam negativos
            const saldoQtdFinal = Math.max(0, saldoQuantidade);
            const saldoValorFinal = Math.max(0, saldoValor);
            
            console.log(`Saldo após detalhe ${index + 1} do item ${item.item}:`, { 
              qtd: saldoQtdFinal, 
              valor: saldoValorFinal 
            });
            
            // Adicionar itemId ao detalhe para facilitar a identificação
            return {
              ...detalhe,
              itemId: item.id,
              quantidade: qtdDetalhe,
              valor: valorDetalhe,
              total: totalDetalhe,
              saldoQuantidade: saldoQtdFinal,
              saldoValor: saldoValorFinal
            };
          });
          
          // Atualizar métricas do item
          item.quantidadeMedida = item.detalhes.reduce((total, d) => total + (parseFloat(d.quantidade) || 0), 0);
          item.totalMedido = item.detalhes.reduce((total, d) => total + (parseFloat(d.total) || 0), 0);
          
          // Calcular progresso (usando a variável qtdTotal já declarada acima)
          if (qtdTotal > 0) {
            item.progresso = Math.min(100, (item.quantidadeMedida / qtdTotal) * 100);
          } else {
            item.progresso = 0;
          }
        }
      });
      
      // Atualizar os itens
      setItens(novosItens);
      
      // Salvar automaticamente
      salvarMedicoes();
      
      toast.success('Medição em lote realizada com sucesso!');
    } catch (error) {
      console.error('Erro ao processar medições em lote:', error);
      toast.error('Erro ao processar medições em lote');
    }
  };
  
  // Calcular saldos para cada detalhe
  const calcularSaldos = (detalhes, itemQuantidade, itemTotal) => {
    if (!detalhes || !Array.isArray(detalhes) || detalhes.length === 0) return [];
    
    try {
      // Garantir que os valores são números
      const qtdTotal = parseFloat(itemQuantidade) || 0;
      const valorTotal = parseFloat(itemTotal) || 0;
      
      // Ordenar por data
      const detalhesSorted = [...detalhes].sort((a, b) => {
        try {
          const dataA = new Date(a.data);
          const dataB = new Date(b.data);
          return dataA - dataB;
        } catch (error) {
          console.error('Erro ao ordenar por data:', error);
          return 0;
        }
      });

      // Inicializar com o valor total do item
      let saldoQuantidade = qtdTotal;
      let saldoValor = valorTotal;

      return detalhesSorted.map(detalhe => {
        try {
          // Garantir que os valores do detalhe são números
          const qtdDetalhe = parseFloat(detalhe.quantidade) || 0;
          const valorDetalhe = parseFloat(detalhe.valor) || 0;
          const totalDetalhe = qtdDetalhe * valorDetalhe;
          
          // Deduzir do saldo a quantidade e valor da medição atual
          saldoQuantidade -= qtdDetalhe;
          saldoValor -= totalDetalhe;
          
          // Garantir que os saldos não sejam negativos
          const saldoQtdFinal = Math.max(0, saldoQuantidade);
          const saldoValorFinal = Math.max(0, saldoValor);
          
          return {
            ...detalhe,
            quantidade: qtdDetalhe,
            valor: valorDetalhe,
            total: totalDetalhe,
            saldoQuantidade: saldoQtdFinal,
            saldoValor: saldoValorFinal
          };
        } catch (error) {
          console.error('Erro ao processar detalhe:', error, detalhe);
          return {
            ...detalhe,
            quantidade: parseFloat(detalhe.quantidade) || 0,
            valor: parseFloat(detalhe.valor) || 0,
            total: parseFloat(detalhe.total) || 0,
            saldoQuantidade: 0,
            saldoValor: 0
          };
        }
      });
    } catch (error) {
      console.error('Erro ao calcular saldos:', error);
      return detalhes.map(detalhe => ({
        ...detalhe,
        saldoQuantidade: 0,
        saldoValor: 0
      }));
    }
  };
  
  // Calcular a quantidade medida (soma das quantidades dos detalhes)
  const calcularQuantidadeMedida = (detalhes) => {
    if (!detalhes || !Array.isArray(detalhes) || detalhes.length === 0) return 0;
    
    // Garantir que estamos somando apenas números válidos
    return detalhes.reduce((total, detalhe) => {
      const quantidade = parseFloat(detalhe.quantidade);
      return total + (isNaN(quantidade) ? 0 : quantidade);
    }, 0);
  };

  // Calcular o total medido (soma dos totais dos detalhes)
  const calcularTotalMedido = (detalhes) => {
    if (!detalhes || !Array.isArray(detalhes) || detalhes.length === 0) return 0;
    
    // Garantir que estamos somando apenas números válidos
    return detalhes.reduce((total, detalhe) => {
      const valorTotal = parseFloat(detalhe.total);
      return total + (isNaN(valorTotal) ? 0 : valorTotal);
    }, 0);
  };

  // Calcular o progresso (quantidade medida / quantidade total)
  const calcularProgresso = (quantidadeMedida, quantidade) => {
    const qtdMedida = parseFloat(quantidadeMedida);
    const qtdTotal = parseFloat(quantidade);
    
    if (isNaN(qtdTotal) || qtdTotal === 0) return 0;
    if (isNaN(qtdMedida)) return 0;
    
    const progresso = (qtdMedida / qtdTotal) * 100;
    return Math.min(100, Math.max(0, progresso)); // Limitar entre 0 e 100
  };

  if (!obra) {
    return <div>Carregando dados da obra...</div>;
  }

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
          <button
            type="button"
            className="button secondary"
            onClick={() => navigate('/obras')}
          >
            <ArrowLeft size={18} />
            Voltar para Lista
          </button>
          <h2>Medições da Obra: {obra.nome}</h2>
        </div>

        {/* Informações da Obra */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '15px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '15px'
        }}>
          <div>
            <strong>Empreiteiro:</strong>
            <div>{obra.empreiteiro}</div>
          </div>
          <div>
            <strong>Valor Contratual:</strong>
            <div>{formatarMoeda(obra.valor)}</div>
          </div>
          <div>
            <strong>Status:</strong>
            <div>{obra.status}</div>
          </div>
          <div>
            <strong>Data de Início:</strong>
            <div>{new Date(obra.dataInicio).toLocaleDateString('pt-BR')}</div>
          </div>
          <div>
            <strong>Previsão de Término:</strong>
            <div>{new Date(obra.dataPrevisaoTermino).toLocaleDateString('pt-BR')}</div>
          </div>
        </div>

        {/* Botões de Ação */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button 
            className="button" 
            onClick={salvarMedicoes}
          >
            Salvar Medições
          </button>
          <button 
            className="button" 
            onClick={() => setModalMedicaoAberto(true)}
            style={{ 
              backgroundColor: '#0d6efd', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px'
            }}
          >
            <FileText size={16} />
            Lançar Medição em Lote
          </button>
          <button 
            className="button secondary" 
            onClick={() => setMostrarRelatorio(!mostrarRelatorio)}
          >
            {mostrarRelatorio ? 'Ocultar Relatório' : 'Mostrar Relatório'}
          </button>
          <button 
            className="button" 
            onClick={exportarItensParaExcel}
            style={{ 
              backgroundColor: '#28a745', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px'
            }}
          >
            <FileDown size={16} />
            Exportar Medições
          </button>
        </div>

        {/* Gerenciador de Itens */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3>Itens e Medições</h3>
          </div>
          
          <div className="card" style={{ padding: '20px', marginBottom: '20px', width: '100%' }}>
            <GerenciadorItens itens={itens} setItens={setItens} />
          </div>
          
          {/* Resumo dos Itens */}
          {itens.length > 0 && (
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#e8f5e9', 
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>Resumo dos Itens</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <div>
                  <strong>Total de Itens:</strong> {itens.length}
                </div>
                <div>
                  <strong>Valor Total:</strong> {formatarMoeda(calcularValorTotalItens(itens))}
                </div>
                <div>
                  <strong>Progresso Geral:</strong> {formatarPercentual(calcularProgressoGeral(itens))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Relatório Gerencial - Condicional */}
        {mostrarRelatorio && id && obra && (
          <div style={{ marginTop: '30px' }}>
            <h3>Relatório Gerencial</h3>
            <div className="card" style={{ padding: '20px', marginBottom: '20px', width: '100%' }}>
              <RelatorioGerencial obraId={id} />
            </div>
          </div>
        )}
      </div>
      
      {/* Modal de Medição em Lote */}
      <ModalMedicaoEmLote 
        isOpen={modalMedicaoAberto}
        onClose={() => setModalMedicaoAberto(false)}
        itens={itens}
        onSalvar={processarMedicoesEmLote}
      />
    </div>
  );
};

export default GerenciadorMedicoes;